import { Injectable } from '@angular/core';
import { API } from 'aws-amplify';
import { defer, forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, startWith, switchMap, tap, timeout } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CONST_AWS_API } from 'src/app/core/constants';
import { groupSuggestions } from './search-suggestion-link';
import { SearchSuggestionGroup, SuggestionScope, SuggestionState } from './search-suggestions.model';

export const SUGGEST_MIN_LENGTH = 2;
export const SUGGEST_DEBOUNCE_MS = 250;
export const SUGGEST_TIMEOUT_MS = 4000;
export const SUGGEST_CACHE_SIZE = 20;
const CONTENT_LIMIT = 8;
const MERCH_LIMIT = 4;

interface SuggestRequest {
  term: string;
  scope: SuggestionScope;
}

function cacheKey({ term, scope }: SuggestRequest): string {
  return [scope.basePath, scope.channelId || 0, scope.networkGroupId || 0, term.toLowerCase()].join('|');
}

/**
 * OpenSearch docs from either endpoint; the body may arrive parsed or as the Lambda's JSON string.
 * A search failure still comes back as a normal response with { error } in the body, so a body
 * without hits throws and is treated (and not cached) as a failed source.
 */
function sourcesOf(response: any): any[] {
  const body = response?.data?.body;
  const parsed = typeof body === 'string' ? JSON.parse(body) : body;
  if (!Array.isArray(parsed?.hits?.hits)) {
    throw new Error('Search failed');
  }
  return parsed.hits.hits.map((hit: any) => hit._source);
}

/**
 * Type-ahead for the top menu search. Nothing is requested until the fan types; each pause in typing
 * makes at most two parallel calls, newer terms drop older responses, and recent terms are cached.
 */
@Injectable({ providedIn: 'root' })
export class SearchSuggestionsService {
  private readonly requests = new Subject<SuggestRequest>();
  private readonly cache = new Map<string, SearchSuggestionGroup[]>();

  readonly state$: Observable<SuggestionState> = this.requests.pipe(
    map(({ term, scope }) => ({ term: (term || '').trim(), scope })),
    debounceTime(SUGGEST_DEBOUNCE_MS),
    distinctUntilChanged((a, b) => cacheKey(a) === cacheKey(b)),
    switchMap((request) => request.term.length < SUGGEST_MIN_LENGTH
      ? of({ term: request.term, loading: false, groups: [] })
      : this.lookup(request)),
  );

  suggest(term: string, scope: SuggestionScope): void {
    this.requests.next({ term, scope });
  }

  private lookup(request: SuggestRequest): Observable<SuggestionState> {
    const key = cacheKey(request);
    const cached = this.cache.get(key);
    if (cached) {
      return of({ term: request.term, loading: false, groups: cached });
    }
    return forkJoin([this.fetchContent(request), this.fetchMerch(request)]).pipe(
      map(([content, merch]) => {
        const groups = groupSuggestions(content, merch, request.scope.basePath, environment.assets_url + 'album-cover.svg');
        // A failed source is not cached, so the next pause on this term asks again.
        if (content && merch) {
          this.remember(key, groups);
        }
        return { term: request.term, loading: false, groups, failed: !content && !merch };
      }),
      startWith({ term: request.term, loading: true }),
    );
  }

  /** Events, videos and audio of this network from the main-search index. */
  private fetchContent({ term, scope }: SuggestRequest): Observable<any[] | null> {
    if (!scope.performerId) {
      return of(null);
    }
    let query = `?q=${encodeURIComponent(term)}&performer_id=${scope.performerId}&start=0&limit=${CONTENT_LIMIT}`;
    if (scope.channelId && scope.channelId != 0) {
      query += `&channel_id=${scope.channelId}`;
    }
    const init = { headers: { 'x-api-key': environment.AWS_Cognito.X_Api_Key }, response: true };
    return this.guard(defer(() => API.get(environment.AWS_Cognito.apiName, CONST_AWS_API.SEARCH + query, init)));
  }

  /** In-stock merchandise collections of this network and group, as the network Merchandise page asks. */
  private fetchMerch({ term, scope }: SuggestRequest): Observable<any[] | null> {
    if (!scope.organizationId) {
      return of(null);
    }
    const init = {
      headers: { 'Content-Type': 'application/json', 'x-api-key': environment.AWS_Cognito.X_Api_Key },
      response: true,
      body: {
        type: 'collection',
        q: term,
        start: 0,
        limit: MERCH_LIMIT,
        filters: [{ name: 'stock', value: 1, organization_id: scope.organizationId, network_group_id: scope.networkGroupId || 0 }],
      },
    };
    return this.guard(defer(() => API.post(environment.AWS_Cognito.apiName, CONST_AWS_API.SEARCH_MARKETLSIT, init)));
  }

  /** A slow or failing source resolves to null so the other groups still show. */
  private guard(call: Observable<any>): Observable<any[] | null> {
    return call.pipe(
      timeout(SUGGEST_TIMEOUT_MS),
      map(sourcesOf),
      catchError(() => of(null)),
    );
  }

  private remember(key: string, groups: SearchSuggestionGroup[]): void {
    this.cache.delete(key);
    this.cache.set(key, groups);
    if (this.cache.size > SUGGEST_CACHE_SIZE) {
      this.cache.delete(this.cache.keys().next().value);
    }
  }
}
